import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

const LABEL_ICONS = {
  Home: '🏠',
  Work: '🏢',
  Airport: '✈️',
  Gym: '🏋️',
  School: '🏫',
  Other: '📍',
};

const LocationInput = ({ pickup, dropoff, onPickupChange, onDropoffChange, service, onGpsDetecting }) => {
  const [pickupFocused, setPickupFocused] = useState(false);
  const [dropoffFocused, setDropoffFocused] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [activeField, setActiveField] = useState(null); // 'pickup' | 'dropoff'

  // Load saved addresses from Supabase
  useEffect(() => {
    const loadSavedAddresses = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) return;
        const { data, error } = await supabase?.from('customer_addresses')?.select('id, label, address, is_favorite, use_count')?.eq('user_id', user?.id)?.order('is_favorite', { ascending: false })?.order('use_count', { ascending: false })?.limit(6);
        if (!error && data) setSavedAddresses(data);
      } catch (err) {
        // non-critical, fallback to empty
      }
    };
    loadSavedAddresses();
  }, []);

  const handleAddressQuickPick = async (addr) => {
    const target = activeField || 'dropoff';
    if (target === 'pickup') {
      onPickupChange(addr?.address);
    } else {
      onDropoffChange(addr?.address);
    }
    // Increment use_count
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase?.from('customer_addresses')?.update({ use_count: (addr?.use_count || 0) + 1 })?.eq('id', addr?.id)?.eq('user_id', user?.id);
      }
    } catch (err) {
      // non-critical
    }
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res?.json();
      if (data?.display_name) {
        // Build a short readable address
        const addr = data?.address;
        const parts = [
          addr?.road || addr?.pedestrian || addr?.footway,
          addr?.suburb || addr?.neighbourhood || addr?.quarter,
          addr?.city || addr?.town || addr?.village,
        ]?.filter(Boolean);
        return parts?.length > 0 ? parts?.join(', ') : data?.display_name?.split(',')?.slice(0, 3)?.join(',')?.trim();
      }
    } catch {
      // fallback to coordinates
    }
    return `${lat?.toFixed(5)}, ${lng?.toFixed(5)}`;
  };

  const detectLocation = () => {
    if (!navigator?.geolocation) {
      setGpsError('Geolocation not supported by your browser');
      return;
    }
    setGpsError(null);
    onGpsDetecting?.(true);
    onPickupChange('Locating you...');

    navigator.geolocation?.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position?.coords;
        const address = await reverseGeocode(latitude, longitude);
        onPickupChange(address);
        onGpsDetecting?.(false);
      },
      (err) => {
        onGpsDetecting?.(false);
        if (err?.code === err?.PERMISSION_DENIED) {
          setGpsError('Location access denied. Please enter your pickup manually.');
          onPickupChange('');
        } else {
          setGpsError('Could not detect location. Please enter manually.');
          onPickupChange('');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  const isLocating = pickup === 'Locating you...';

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-[#0a1628]/60 uppercase tracking-wider">
        {service === 'delivery' ? 'Pickup & Delivery Location' : 'Where to?'}
      </h3>
      {/* Location Fields */}
      <div className="relative bg-white rounded-2xl border border-[#0a1628]/10 shadow-sm overflow-hidden">
        {/* Pickup */}
        <div className={`flex items-center gap-3 px-4 py-3.5 transition-colors ${pickupFocused ? 'bg-[#ffd700]/5' : ''}`}>
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <div className={`w-3 h-3 rounded-full border-2 border-[#0a1628] transition-all ${isLocating ? 'bg-green-400 animate-pulse' : 'bg-[#ffd700]'}`}></div>
            <div className="w-0.5 h-5 bg-[#0a1628]/20 rounded-full"></div>
          </div>
          <div className="flex-1 min-w-0">
            {isLocating ? (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full border-2 border-green-400 border-t-transparent animate-spin flex-shrink-0" />
                <span className="text-sm font-medium text-green-600">Locating you...</span>
              </div>
            ) : (
              <input
                type="text"
                value={pickup}
                onChange={(e) => onPickupChange(e?.target?.value)}
                onFocus={() => { setPickupFocused(true); setActiveField('pickup'); }}
                onBlur={() => setPickupFocused(false)}
                placeholder="Pickup location"
                className="w-full text-sm font-medium text-[#0a1628] placeholder-[#0a1628]/40 bg-transparent border-none outline-none focus:ring-0 p-0"
              />
            )}
          </div>
          <button
            onClick={detectLocation}
            disabled={isLocating}
            className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${isLocating ? 'bg-green-100 cursor-not-allowed' : 'bg-[#0a1628]/5 hover:bg-[#ffd700]/20'}`}
            title="Use current location"
          >
            {isLocating ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" className="w-4 h-4">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v4M12 19v4M1 12h4M19 12h4" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-[#0a1628]/60">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v4M12 19v4M1 12h4M19 12h4" />
              </svg>
            )}
          </button>
        </div>

        {/* Divider */}
        <div className="h-px bg-[#0a1628]/8 mx-4"></div>

        {/* Dropoff */}
        <div className={`flex items-center gap-3 px-4 py-3.5 transition-colors ${dropoffFocused ? 'bg-[#ffd700]/5' : ''}`}>
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <div className="w-0.5 h-5 bg-[#0a1628]/20 rounded-full"></div>
            <div className="w-3 h-3 rounded-sm bg-[#0a1628] rotate-45"></div>
          </div>
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={dropoff}
              onChange={(e) => onDropoffChange(e?.target?.value)}
              onFocus={() => { setDropoffFocused(true); setActiveField('dropoff'); }}
              onBlur={() => setDropoffFocused(false)}
              placeholder={service === 'delivery' ? 'Delivery destination' : 'Drop-off location'}
              className="w-full text-sm font-medium text-[#0a1628] placeholder-[#0a1628]/40 bg-transparent border-none outline-none focus:ring-0 p-0"
            />
          </div>
          {dropoff && (
            <button
              onClick={() => onDropoffChange('')}
              className="flex-shrink-0 p-1.5 rounded-lg bg-[#0a1628]/5 hover:bg-red-50 transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-[#0a1628]/40">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      {/* GPS error message */}
      {gpsError && (
        <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
          <svg viewBox="0 0 24 24" fill="#d97706" className="w-4 h-4 flex-shrink-0 mt-0.5">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <p className="text-xs text-amber-700 font-medium">{gpsError}</p>
        </div>
      )}
      {/* Saved Addresses Quick Access */}
      {savedAddresses?.length > 0 ? (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold text-[#0a1628]/40 uppercase tracking-wider px-0.5">
            Saved addresses · tap to fill {activeField === 'pickup' ? 'pickup' : 'drop-off'}
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {savedAddresses?.map((addr) => (
              <button
                key={addr?.id}
                onClick={() => handleAddressQuickPick(addr)}
                className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-white rounded-xl border border-[#0a1628]/10 hover:border-[#ffd700] hover:bg-[#ffd700]/5 transition-all text-xs font-medium text-[#0a1628]/70 hover:text-[#0a1628]"
              >
                <span>{LABEL_ICONS?.[addr?.label] || '📍'}</span>
                <span>{addr?.label}</span>
                {addr?.is_favorite && <span className="text-[#ffd700]">★</span>}
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* Fallback static quick picks when no saved addresses */
        (<div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { label: 'Home', address: 'Westlands, Nairobi', icon: '🏠' },
            { label: 'Work', address: 'CBD, Nairobi', icon: '🏢' },
            { label: 'Airport', address: 'JKIA, Nairobi', icon: '✈️' },
          ]?.map((loc) => (
            <button
              key={loc?.label}
              onClick={() => onDropoffChange(loc?.address)}
              className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-white rounded-xl border border-[#0a1628]/10 hover:border-[#ffd700] hover:bg-[#ffd700]/5 transition-all text-xs font-medium text-[#0a1628]/70 hover:text-[#0a1628]"
            >
              <span>{loc?.icon}</span>
              <span>{loc?.label}</span>
            </button>
          ))}
        </div>)
      )}
    </div>
  );
};

export default LocationInput;
